import { formatTime } from '../../utils/calendar-utils'
import type { GridSegment } from '../../utils/calendar-utils'
import {
    SLOT_HEIGHT_PX,
    COLLAPSE_HANDLE_HEIGHT_PX,
} from '../../utils/calendar-utils'
import { MdUnfoldMore, MdUnfoldLess } from 'react-icons/md'

interface TimeSlotProps {
    time: Date
    onClick?: (time: Date) => void
    disabled?: boolean
    children?: React.ReactNode
}

export function TimeSlot({ time, onClick, disabled, children }: TimeSlotProps) {
    const handleClick = () => {
        if (!disabled && onClick) {
            onClick(time)
        }
    }

    return (
        <div
            className={`min-h-[60px] border-b border-base-200 relative ${
                disabled
                    ? 'bg-base-200/50 cursor-not-allowed'
                    : 'hover:bg-base-200/30 cursor-pointer'
            }`}
            onClick={handleClick}
        >
            {children}
        </div>
    )
}

interface TimeColumnProps {
    times: Date[]
}

export function TimeColumn({ times }: TimeColumnProps) {
    return (
        <div className="w-16 flex-shrink-0 border-r border-base-300">
            {times.map((time, index) => (
                <div
                    key={index}
                    className="h-[60px] flex items-start justify-end pr-2 text-xs text-base-content/60"
                >
                    {formatTime(time)}
                </div>
            ))}
        </div>
    )
}

interface TimeRulerProps {
    segments: GridSegment[]
}

/**
 * Régua de horários que acompanha os trechos recolhidos.
 *
 * Nasce dos MESMOS segmentos da grade, senão o rótulo e a linha do horário se
 * descolam assim que um intervalo vazio é recolhido.
 */
export function TimeRuler({ segments }: TimeRulerProps) {
    return (
        <div className="w-16 flex-shrink-0 border-r border-base-300">
            {segments.map(segment =>
                segment.type === 'gap' ? (
                    <div
                        key={segment.key}
                        className="flex items-center justify-end pr-2 text-[10px] text-base-content/40"
                        style={{ height: `${segment.height}px` }}
                    >
                        {formatTime(segment.slots[0])}
                    </div>
                ) : (
                    <div key={segment.key}>
                        {segment.collapsible && (
                            <div
                                style={{
                                    height: `${COLLAPSE_HANDLE_HEIGHT_PX}px`,
                                }}
                            />
                        )}
                        {segment.slots.map((time, index) => (
                            <div
                                key={index}
                                className="h-[60px] flex items-start justify-end pr-2 text-xs text-base-content/60"
                            >
                                {formatTime(time)}
                            </div>
                        ))}
                    </div>
                )
            )}
        </div>
    )
}

interface CollapsedGapProps {
    segment: GridSegment
    /** Só a coluna mais larga tem espaço para o texto; nas estreitas, só o ícone. */
    showLabel?: boolean
    onExpand: (key: string) => void
}

export function CollapsedGap({
    segment,
    showLabel = true,
    onExpand,
}: CollapsedGapProps) {
    // `endMinutes` é minuto do dia: parte-se da meia-noite do próprio slot para
    // não depender da duração de cada faixa.
    const gapEnd = new Date(segment.slots[0])
    gapEnd.setHours(0, 0, 0, 0)
    gapEnd.setMinutes(segment.endMinutes)

    const from = formatTime(segment.slots[0])
    const to = formatTime(gapEnd)

    return (
        <button
            type="button"
            className="w-full flex items-center justify-center gap-1 border-b border-base-200 bg-base-200/30 text-xs text-base-content/50 hover:bg-base-200/60 hover:text-base-content/80 transition-colors"
            style={{ height: `${segment.height}px` }}
            onClick={() => onExpand(segment.key)}
            title={`Mostrar horários livres de ${from} às ${to}`}
        >
            <MdUnfoldMore className="text-sm" />
            {showLabel && (
                <span>
                    {from} - {to} livre
                </span>
            )}
        </button>
    )
}

interface DayGridProps {
    segments: GridSegment[]
    isPast?: boolean
    showGapLabel?: boolean
    onSlotClick?: (time: Date) => void
    onExpandGap: (key: string) => void
    onCollapseGap: (key: string) => void
}

/** Fundo da coluna de um dia: slots clicáveis e faixas recolhidas. */
export function DayGrid({
    segments,
    isPast = false,
    showGapLabel = true,
    onSlotClick,
    onExpandGap,
    onCollapseGap,
}: DayGridProps) {
    return (
        <>
            {segments.map(segment =>
                segment.type === 'gap' ? (
                    <CollapsedGap
                        key={segment.key}
                        segment={segment}
                        showLabel={showGapLabel}
                        onExpand={onExpandGap}
                    />
                ) : (
                    <div key={segment.key}>
                        {segment.collapsible && (
                            <button
                                type="button"
                                className="w-full flex items-center justify-center gap-1 bg-base-200/30 text-[10px] text-base-content/50 hover:bg-base-200/60 hover:text-base-content/80 transition-colors"
                                style={{
                                    height: `${COLLAPSE_HANDLE_HEIGHT_PX}px`,
                                }}
                                onClick={() => onCollapseGap(segment.key)}
                                title="Recolher os horários livres"
                            >
                                <MdUnfoldLess className="text-xs" />
                                {showGapLabel && <span>Recolher</span>}
                            </button>
                        )}
                        {segment.slots.map((time, index) => (
                            <div
                                key={index}
                                className={`border-b border-base-200 ${
                                    isPast
                                        ? 'bg-base-200/50 cursor-not-allowed'
                                        : 'hover:bg-base-200/30 cursor-pointer'
                                }`}
                                style={{ height: `${SLOT_HEIGHT_PX}px` }}
                                onClick={() => onSlotClick?.(time)}
                            />
                        ))}
                    </div>
                )
            )}
        </>
    )
}
